/**
 * Settings → Ad Account → Campaign Groups.
 *
 * In Apple Search Ads a "Campaign Group" IS the API orgId. One Apple
 * identity can have access to several (e.g. a separate group per app).
 * asa_integrations holds ONE orgId (the primary); Discover lists the rest
 * via Platform API v1 `/v1/acls` and the admin picks which to sync. Each
 * group carries its access level (Read & Write / Read only). A group already
 * connected in another OneAds workspace comes back `blocked` and can't be
 * enabled — one Apple Ads account lives in exactly one workspace.
 */

import { useEffect, useState } from "react";
import {
  useAsaCampaignGroups,
  useDiscoverAsaCampaignGroups,
  useSetAsaCampaignGroups,
  type AsaCampaignGroup,
} from "@/lib/api-hooks";
import { AccessPill, CheckMark } from "./ad-account-ui";

function errMsg(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { error?: { message?: string } } } })?.response
      ?.data?.error?.message ?? fallback
  );
}

export function AsaCampaignGroupsCard({ appleMethod: _appleMethod }: { appleMethod?: boolean }) {
  const query = useAsaCampaignGroups();
  const discover = useDiscoverAsaCampaignGroups();
  const save = useSetAsaCampaignGroups();

  const [enabledIds, setEnabledIds] = useState<Set<number>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!query.data) return;
    setEnabledIds(new Set(query.data.filter((g) => g.enabled && !g.blocked).map((g) => g.asa_org_id)));
    setDirty(false);
  }, [query.data]);

  const groups = query.data ?? [];

  const toggle = (g: AsaCampaignGroup) => {
    if (g.is_primary || g.blocked) return; // primary is always synced
    setSaved(false);
    setErr(null);
    setDirty(true);
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(g.asa_org_id)) next.delete(g.asa_org_id);
      else next.add(g.asa_org_id);
      return next;
    });
  };

  const onDiscover = async () => {
    setErr(null);
    setSaved(false);
    try {
      await discover.mutateAsync();
    } catch (e) {
      setErr(errMsg(e, "Discovery failed."));
    }
  };

  const onSave = async () => {
    setErr(null);
    setSaved(false);
    try {
      const ids = groups
        .filter((g) => !g.is_primary && !g.blocked && enabledIds.has(g.asa_org_id))
        .map((g) => g.asa_org_id);
      await save.mutateAsync(ids);
      setSaved(true);
      setDirty(false);
    } catch (e) {
      setErr(errMsg(e, "Save failed."));
    }
  };

  return (
    <div className="aa-card aa-card-20 aa-mb">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h3 className="aa-h3">Campaign Groups</h3>
          <div style={{ fontSize: 12, marginTop: 4, maxWidth: 540, color: "var(--c-text-3)" }}>
            One Apple Search Ads account can hold several Campaign Groups (org IDs) — for example a separate
            group per app. Pick which to sync into this workspace; the primary group is always synced.
          </div>
        </div>
        <button
          type="button"
          className="aa-btn"
          style={{ background: "transparent", borderColor: "transparent" }}
          onClick={() => void onDiscover()}
          disabled={discover.isPending}
        >
          {discover.isPending ? "Discovering…" : "Discover"}
        </button>
      </div>

      {groups.length === 0 ? (
        <div style={{ marginTop: 12, fontSize: 13, color: "var(--c-text-3)" }}>
          {query.isLoading ? "Loading…" : "No groups discovered yet — click Discover."}
        </div>
      ) : (
        <div className="aa-rows aa-rows-compact" style={{ marginTop: 14 }}>
          {groups.map((g) => {
            const on = !g.blocked && (g.is_primary || enabledIds.has(g.asa_org_id));
            return (
              <div key={g.asa_org_id} className={`aa-row${g.blocked ? " aa-row-blocked" : ""}`}>
                <button
                  type="button"
                  className={`aa-cb${on ? " on" : ""}`}
                  onClick={() => toggle(g)}
                  disabled={g.is_primary || g.blocked}
                  aria-pressed={on}
                >
                  {on && <CheckMark />}
                </button>
                <div className="aa-row-name" onClick={() => toggle(g)}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{g.org_name ?? `Org ${g.asa_org_id}`}</span>
                  <span className="aa-org">Org ID {g.asa_org_id}</span>
                  {g.blocked && <span className="aa-blocked">Already connected to another OneAds workspace. Disconnect it there to connect it here.</span>}
                </div>
                <div>
                  <AccessPill level={g.access_level ?? "read_write"} />
                </div>
                <div>{g.is_primary && <span className="aa-pill aa-pill-plain">Primary</span>}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <button
          type="button"
          className="aa-btn aa-btn-dark"
          style={{ padding: "0 14px" }}
          onClick={() => void onSave()}
          disabled={save.isPending || groups.length === 0 || !dirty}
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: "var(--c-pos-600)" }}>
            Saved — enabled groups sync on the next collector tick.
          </span>
        )}
        {err && <span style={{ fontSize: 12, color: "var(--c-neg-600)" }}>{err}</span>}
      </div>
    </div>
  );
}
